import * as Yup from 'yup';
import { emailDomains } from '../utils/blockedEmail';

// Define the user change password
const userChangePasswordSchema = Yup.object().shape({
  OPassword: Yup.string().required("Old Password is required"),
  NPassword: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .test(
      'special-characters',
      'Password must contain at least one special character(!@#$%^&*?)',
      (value) => {
        return /[!@#$%^&*?]/.test(value);
      }
    )
    .test(
      'uppercase-letter',
      'Password must contain at least one uppercase letter',
      (value) => {
        return /[A-Z]/.test(value);
      }
    )
    .test(
      'lowercase-letter',
      'Password must contain at least one lowercase letter',
      (value) => {
        return /[a-z]/.test(value);
      }
    )
    .test(
      'digit',
      'Password must contain at least one digit',
      (value) => {
        return /\d/.test(value);
      }
    )
    .required("New Password is required"),
  CPassword: Yup.string().oneOf([Yup.ref('NPassword'), null], 'Passwords must match'),
});
// Define the user schema
const userSchema = Yup.object().shape({
  user_ID: Yup.number(),
  Company_ID: Yup.number(),
  Site_ID: Yup.string().typeError("Please select site").required("Site is required"),
  First_name: Yup.string()
    .min(3, "First name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "First name must contain only alphabetic characters, spaces, and special characters")
    .required("First name is required"),
  Last_name: Yup.string()
    .min(3, "Last name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "Last name must contain only alphabetic characters, spaces, and special characters")
    .required("Last name is required"),
  Position: Yup.string().required("Position is required"),
  Email: Yup.string()
    .email("Email is not valid")
    .test(
      'Email',
      'Personal email addresses are not allowed',
      (value) => {
        if (!value) return true; // Skip if value is not provided
        const emailDomain = value.split('@')[1].split('.')[0]; // Extract the specific part between '@' and '.'
        return !emailDomains.includes(emailDomain);
      }
    )
    .required("Email is required"),
  Phone: Yup.number().typeError("Phone number is not valid").required("Phone number is required"),
  Role: Yup.string().typeError("Role must be admin or analyst / user").required("Role is required"),
  Password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .test(
      'special-characters',
      'Password must contain at least one special character(!@#$%^&*?)',
      (value) => {
        return /[!@#$%^&*?]/.test(value);
      }
    )
    .test(
      'uppercase-letter',
      'Password must contain at least one uppercase letter',
      (value) => {
        return /[A-Z]/.test(value);
      }
    )
    .test(
      'lowercase-letter',
      'Password must contain at least one lowercase letter',
      (value) => {
        return /[a-z]/.test(value);
      }
    )
    .test(
      'digit',
      'Password must contain at least one digit',
      (value) => {
        return /\d/.test(value);
      }
    )
    .required("Password is required"),
  CPassword: Yup.string().oneOf([Yup.ref('Password'), null], 'Passwords must match'),
  Notifications: Yup.boolean(),
  Language: Yup.string()
    .oneOf(["English", "French", "Arabic"], "Invalid language")
    .required("Language is required"),
});

// Define the user signup schema
const userSignupSchema = Yup.object().shape({
  First_name: Yup.string()
    .min(3, "First name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "First name must contain only alphabetic characters, spaces, and special characters")
    .required("First name is required"),
  Last_name: Yup.string()
    .min(3, "Last name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "Last name must contain only alphabetic characters, spaces, and special characters")
    .required("Last name is required"),
  Position: Yup.string().required("Position is required"),
  Email: Yup.string()
    .email("Email is not valid")
    .test(
      'Email',
      'Personal email addresses are not allowed',
      (value) => {
        if (!value) return true; // Skip if value is not provided
        const emailDomain = value.split('@')[1].split('.')[0]; // Extract the specific part between '@' and '.'
        return !emailDomains.includes(emailDomain);
      }
    )
    .required("Email is required"),
  Phone: Yup.number().typeError("Phone number is not valid").required("Phone number is required"),
  Password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .test(
      'special-characters',
      'Password must contain at least one special character(!@#$%^&*?)',
      (value) => {
        return /[!@#$%^&*?]/.test(value);
      }
    )
    .test(
      'uppercase-letter',
      'Password must contain at least one uppercase letter',
      (value) => {
        return /[A-Z]/.test(value);
      }
    )
    .test(
      'lowercase-letter',
      'Password must contain at least one lowercase letter',
      (value) => {
        return /[a-z]/.test(value);
      }
    )
    .test(
      'digit',
      'Password must contain at least one digit',
      (value) => {
        return /\d/.test(value);
      }
    )
    .required("Password is required"),
  CPassword: Yup.string().oneOf([Yup.ref('Password'), null], 'Passwords must match'),
  Language: Yup.string()
    .oneOf(["English", "French", "Arabic"], "Invalid language")
    .required("Language is required"),
});
// Define the user edit schema
const userEditSchema = Yup.object().shape({
  user_ID: Yup.number(),
  Company_ID: Yup.number(),
  Site_ID: Yup.string().typeError("Please select site").required("Site is required"),
  First_name: Yup.string()
    .min(3, "First name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "First name must contain only alphabetic characters, spaces, and special characters")
    .required("First name is required"),
  Last_name: Yup.string()
    .min(3, "Last name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "Last name must contain only alphabetic characters, spaces, and special characters")
    .required("Last name is required"),
  Position: Yup.string().required("Position is required"),
  Email: Yup.string()
    .email("Email is not valid")
    .test(
      'Email',
      'Personal email addresses are not allowed',
      (value) => {
        if (!value) return true; // Skip if value is not provided
        const emailDomain = value.split('@')[1].split('.')[0]; // Extract the specific part between '@' and '.'
        return !emailDomains.includes(emailDomain);
      }
    )
    .required("Email is required"),
  Phone: Yup.number().typeError("Phone number is not valid").required("Phone number is required"),
  Role: Yup.string().typeError("Role must be admin or analyst").required("Role is required"),
  Password: Yup.string()
    .notRequired()
    .transform((value) => !!value ? value : null)
    .min(8, "Password must be at least 8 characters long")
    .nullable()
    .test(
      'special-characters',
      'Password must contain at least one special character(!@#$%^&*?)',
      (value) => {
        return /[!@#$%^&*?]/.test(value);
      }
    )
    .test(
      'uppercase-letter',
      'Password must contain at least one uppercase letter',
      (value) => {
        return /[A-Z]/.test(value);
      }
    )
    .test(
      'lowercase-letter',
      'Password must contain at least one lowercase letter',
      (value) => {
        return /[a-z]/.test(value);
      }
    )
    .test(
      'digit',
      'Password must contain at least one digit',
      (value) => {
        return /\d/.test(value);
      }
    ),

  CPassword: Yup.string().oneOf([Yup.ref('Password'), null], 'Passwords must match'),
  Notifications: Yup.boolean(),
  Language: Yup.string()
    .oneOf(["English", "French", "Arabic"], "Invalid language")
    .required("Language is required"),
});
const userEditSchemaNoPassW = Yup.object().shape({
  user_ID: Yup.number(),
  Company_ID: Yup.number(),
  Site_ID: Yup.string().nullable(),
  First_name: Yup.string()
    .min(3, "First name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "First name must contain only alphabetic characters, spaces, and special characters")
    .required("First name is required"),
  Last_name: Yup.string()
    .min(3, "Last name must be at least 3 characters long")
    .matches(/^[A-Za-z\séç']+$/, "Last name must contain only alphabetic characters, spaces, and special characters")
    .required("Last name is required"),
  Position: Yup.string().required("Position is required"),
  Email: Yup.string()
    .email("Email is not valid")
    .test(
      'Email',
      'Personal email addresses are not allowed',
      (value) => {
        if (!value) return true; // Skip if value is not provided
        const emailDomain = value.split('@')[1].split('.')[0]; // Extract the specific part between '@' and '.'
        return !emailDomains.includes(emailDomain);
      }
    )
    .required("Email is required"),
  Phone: Yup.string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/, "Phone number is not valid")
    .min(8, "Phone number is too short")
    .required("Phone number is required"),
  Role: Yup.string().typeError("Role must be admin or analyst").required("Role is required"),
  Notifications: Yup.boolean(),
  SMSNotifications: Yup.boolean(),
  emailNotifications: Yup.boolean(),
  slackNotifications: Yup.boolean(),
  Language: Yup.string()
    .oneOf(["English", "French", "Arabic"], "Invalid language")
    .required("Language is required"),
});
// Define the validateUserChangePassword method to validate user data against the userChangePasswordSchema
export const validateUserChangePassword = (userData) => {
  return userChangePasswordSchema.validate(userData, { abortEarly: false });
};
// Define the validateUser method to validate user data against the userSchema
export const validateUser = (userData) => {
  return userSchema.validate(userData, { abortEarly: false });
};
// Define the validateUser method to validate user data against the userSchema
export const validateUserEdit = (userData) => {
  return userEditSchema.validate(userData, { abortEarly: false });
};

// Define the validateUser method to validate user data against the userSchema
export const validateUserEditNoPassW = (userData) => {
  return userEditSchemaNoPassW.validate(userData, { abortEarly: false });
};

// Define the validateUserSignup method to validate user signup data against the userSignupSchema
export const validateUserSignup = (userData) => {
  return userSignupSchema.validate(userData, { abortEarly: false });
};

// Export userSchema and userSignupSchema
export { userSchema, userSignupSchema };